import { useEffect, useState } from 'react';
import { vendorApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { Building2, Star, TrendingUp, Sparkles, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';

function StatTile({ icon: Icon, label, value, color }) {
  return (
    <Card>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
          <Icon size={20} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{label}</p>
          <p className="text-2xl font-bold text-foreground tabular-nums">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function VendorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    vendorApi.getStats()
      .then(({ data }) => { if (!cancelled) setStats(data); })
      .catch(() => null)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const appName = user?.vendor?.app_name || 'Vendor';

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="vendor-dashboard">
      <header>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Welcome back, {user?.name}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {appName} reseller workspace — managing {stats?.total_clients ?? '—'} client{stats?.total_clients === 1 ? '' : 's'}.
        </p>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile icon={Building2} label="Total Clients" value={stats?.total_clients ?? 0} color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" />
          <StatTile icon={Sparkles} label="Active Clients" value={stats?.active_clients ?? 0} color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" />
          <StatTile icon={Star} label="Total Reviews" value={stats?.total_reviews ?? 0} color="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400" />
          <StatTile icon={TrendingUp} label="New This Week" value={stats?.new_this_week ?? 0} color="bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400" />
        </div>
      )}

      <Card>
        <CardContent className="p-6 space-y-2">
          <h2 className="text-base font-semibold">Getting started</h2>
          <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal pl-5">
            <li><strong className="text-foreground">Set your brand</strong> in Settings — choose your app name, login URL, primary color, and logo.</li>
            <li><strong className="text-foreground">Create your first client</strong> from the Clients page. They'll receive their own login key.</li>
            <li>Send your clients to <code className="font-mono text-xs">/vendor/{user?.vendor?.login_slug || '<slug>'}/login</code> — they'll see your branding from the first screen.</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
